import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { getGetEnginePlayMysessionQueryKey } from '@/types/origin-point-engine'
import { toast } from 'sonner'
import { useSessionsStore } from '../stores/sessions-store'
import { type UserSession, EngineStatus } from '../types/sessionList'
import { useSessions } from './useSessions'

export const useSessionsAction = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { sessionList: rawSessionList, endSessionAsync } = useSessions()

  // Store setters
  const setDeleteDialogOpen = useSessionsStore((s) => s.setDeleteDialogOpen)
  const deleteDialog = useSessionsStore((s) => s.deleteDialog)

  // 将原始数据转换为 UserSession 列表
  const sessionList = useMemo((): UserSession[] => {
    if (!rawSessionList?.sessions) return []

    return rawSessionList.sessions.map((session) => ({
      sessionId: session.id ?? '',
      engine: {
        engineId: session.engineId ?? '',
        engineTitle: session.engineTitle ?? '',
        // API 暂不返回封面 URL，类型上可能无此字段
        engineCoverURL: session.imageUrl ?? '',
        type: EngineStatus.PUBLISHED,
        // API 暂不返回背景图片 URL，类型上可能无此字段
        engineBackgroundImageURL: session.backgroundImageUrl ?? '',
      },
    }))
  }, [rawSessionList])

  // 导航到会话详情页
  // 注意：engine 数据由 useEngine hook 在 Chat 页面自动获取并同步到 store
  const navigateToSession = (session: UserSession) => {
    navigate({
      to: '/chats/$sessionId',
      params: { sessionId: session.sessionId },
    })
  }

  // 打开删除确认对话框
  const openDeleteDialog = (sessionId: string) => {
    setDeleteDialogOpen(true, sessionId)
  }

  // 关闭删除确认对话框
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false)
  }

  // 确认删除会话
  const confirmDeleteSession = async () => {
    const sessionId = deleteDialog.targetSessionId
    if (!sessionId) return

    try {
      await endSessionAsync({ data: { sessionId } })
      toast.success('会话已删除')
      // 刷新会话列表
      queryClient.invalidateQueries({
        queryKey: getGetEnginePlayMysessionQueryKey(),
      })
    } catch {
      toast.error('删除失败，请重试')
    }
  }

  // 获取草稿引擎会话（编辑器预览用）
  const getDraftEngineSession = async (
    _engineId: string,
    _isDraftEngine: boolean
  ): Promise<string> => {
    // TODO: 获取当前用户的，之前编辑的过的草稿引擎会话
    return ''
  }

  return {
    sessionList,
    navigateToSession,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDeleteSession,
    getDraftEngineSession,
  }
}
